import { useEffect, useState } from 'react';
import { FLAGS, useVariant } from '../../lib/flags';
import { posthog } from '../../lib/posthog';

/**
 * The home panel A/B test: what the wide side panel shows when no letter is
 * open. `desk` is the control — the safe default whenever flags have not
 * loaded, the flag is missing, or it holds a value this build doesn't know.
 */
export const HOME_PANEL_VARIANTS = ['desk', 'noticeboard'] as const;
export type HomePanelVariant = (typeof HOME_PANEL_VARIANTS)[number];

/** `?home_panel=noticeboard` previews a variant without touching PostHog. */
export const HOME_PANEL_OVERRIDE_PARAM = 'home_panel';

function known(v: string | null | undefined): HomePanelVariant | null {
  return HOME_PANEL_VARIANTS.find((k) => k === v) ?? null;
}

/** The override wins, then the flag, then `desk`. Pure, for the tests. */
export function resolveHomePanelVariant(flag: string | undefined, search: string): HomePanelVariant {
  const override = known(new URLSearchParams(search).get(HOME_PANEL_OVERRIDE_PARAM));
  return override ?? known(flag) ?? 'desk';
}

export function hasHomePanelOverride(search: string): boolean {
  return known(new URLSearchParams(search).get(HOME_PANEL_OVERRIDE_PARAM)) !== null;
}

/** How long to wait for flags before counting the view as the default. */
const FLAGS_WAIT_MS = 3000;

/**
 * True once the variant shown is the one this person will keep seeing: flags
 * have loaded, an override is set, analytics is off, or we've waited long
 * enough. The view event waits for it, so it carries the right variant.
 */
function useFlagsSettled(overridden: boolean): boolean {
  const [settled, setSettled] = useState(
    () => overridden || !posthog.__loaded || posthog.featureFlags?.hasLoadedFlags === true,
  );
  useEffect(() => {
    if (settled) return;
    const off = posthog.onFeatureFlags(() => setSettled(true));
    const t = setTimeout(() => setSettled(true), FLAGS_WAIT_MS);
    return () => {
      off();
      clearTimeout(t);
    };
  }, [settled]);
  return settled;
}

export function useHomePanelVariant(): { variant: HomePanelVariant; settled: boolean } {
  // Read where the panel is shown: reading the flag records the exposure.
  const flag = useVariant(FLAGS.homePanel);
  const search = typeof window === 'undefined' ? '' : window.location.search;
  const overridden = hasHomePanelOverride(search);
  const settled = useFlagsSettled(overridden);
  return { variant: resolveHomePanelVariant(flag, search), settled };
}

/** Every CTA on the panel, named once so the PostHog breakdown stays tidy. */
export type HomePanelAction =
  | 'share_toiyp'
  | 'copy_address'
  | 'show_qr'
  | 'profile_step'
  | 'todo_item'
  | 'show_done'
  | 'gift_stamp_book'
  | 'open_unread'
  | 'open_stamp_book'
  | 'whats_new'
  | 'whats_new_next'
  | 'in_the_post_open'
  | 'open_desk'
  | 'nudge_dismiss'
  | 'nudge_see_all'
  | 'copy_bio_line'
  | 'today_task'
  | 'enable_push';

/**
 * Where it was seen: the wide panel, the phone strip (desk), the phone Desk tab
 * (noticeboard), or the one nudge in the list on short phones (both variants).
 */
export type HomePanelSurface = 'panel' | 'mobile_strip' | 'mobile_desk' | 'mobile_nudge';

export function trackHomePanelViewed(variant: HomePanelVariant, surface: HomePanelSurface): void {
  posthog.capture('home_panel_viewed', { variant, surface });
}

export function trackHomePanelAction(
  variant: HomePanelVariant,
  action: HomePanelAction,
  extra?: Record<string, string>,
): void {
  posthog.capture('home_panel_action_clicked', { variant, action, ...extra });
}
